import { z } from 'zod';
import { reusePolicySchema } from './reuse';
import { AUDIENCES, audienceScopeSchema } from './audience';
import { timeZoneSchema } from './time';
import { levelSchema, tagsSchema, topicsSchema } from './topics';

/*
 * Self-assessment quizzes. Questions live in a bank owned by an org (or by
 * Nirvik when ownerOrgId is null). Publishing a quiz freezes its questions
 * into an immutable version; attempts always point at a version.
 */

/** Built-in skills for cyber readiness content, with labels and advice. */
export const SKILLS = [
  'phishing',
  'passwords',
  'privacy',
  'device_safety',
  'social_engineering',
  'safe_browsing',
] as const;
/**
 * A skill is a short slug. The built-in cyber skills above have labels and
 * advice; any other topic (e.g. `tool_use`, `rag`) works too and is shown
 * through skillLabel(), so courses and AI-generated questions can cover any
 * subject.
 */
export const skillSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z][a-z0-9_]{1,39}$/, 'Use a short lowercase slug, e.g. tool_use');
export type Skill = string;
export type BuiltInSkill = (typeof SKILLS)[number];

export const SKILL_LABELS: Record<BuiltInSkill, string> = {
  phishing: 'Phishing',
  passwords: 'Passwords and accounts',
  privacy: 'Privacy',
  device_safety: 'Device safety',
  social_engineering: 'Social engineering',
  safe_browsing: 'Safe browsing',
};

/** Shown on results for skills scored under 70%. */
export const SKILL_ADVICE: Record<BuiltInSkill, string> = {
  phishing:
    'Check the sender address, hover over links before clicking, and be wary of urgency. When in doubt, contact the sender another way.',
  passwords:
    'Use a password manager, a unique password for every account, and turn on multi-factor authentication, ideally a passkey or authenticator app.',
  privacy:
    'Share less by default: review app permissions, lock down social profiles, and think before posting personal details or location.',
  device_safety:
    'Keep your operating system and apps updated, lock your screen, and only install software from official stores.',
  social_engineering:
    'Verify unexpected requests for money, codes or access through a channel you trust. Real IT staff never ask for your password or one-time code.',
  safe_browsing:
    "Look for the right domain, not just the padlock. Avoid entering credentials on public Wi-Fi without a VPN, and don't download files from unknown sites.",
};

const isBuiltIn = (skill: string): skill is BuiltInSkill =>
  (SKILLS as readonly string[]).includes(skill);

/** Words written a particular way in skill names (slugs are lowercase). */
const SKILL_WORDS: Record<string, string> = {
  ai: 'AI',
  api: 'API',
  apis: 'APIs',
  aws: 'AWS',
  cli: 'CLI',
  css: 'CSS',
  devops: 'DevOps',
  genai: 'GenAI',
  html: 'HTML',
  iam: 'IAM',
  iot: 'IoT',
  llm: 'LLM',
  llms: 'LLMs',
  mcp: 'MCP',
  ml: 'ML',
  otel: 'OTel',
  rag: 'RAG',
  sql: 'SQL',
  ui: 'UI',
  ux: 'UX',
};
/** Word pairs that read as one hyphenated word ("multi_agent" → "Multi-agent"). */
const SKILL_PREFIXES = new Set(['multi', 'non', 'self', 'cross', 'real', 'end']);

/**
 * Display name: the built-in label, else the slug in sentence case with
 * acronyms kept ("tools_and_mcp" → "Tools and MCP", "multi_agent_systems" →
 * "Multi-agent systems").
 */
export function skillLabel(skill: string): string {
  if (isBuiltIn(skill)) return SKILL_LABELS[skill];
  const words = skill.split('_').filter(Boolean);
  const out: string[] = [];
  for (const [i, w] of words.entries()) {
    const word = SKILL_WORDS[w] ?? w;
    if (i > 0 && SKILL_PREFIXES.has(words[i - 1]!) && out.length) out[out.length - 1] += `-${word}`;
    else out.push(word);
  }
  const text = out.join(' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Advice for a weak skill; generic for skills without built-in advice. */
export function skillAdvice(skill: string): string {
  return isBuiltIn(skill)
    ? SKILL_ADVICE[skill]
    : `Review the material on ${skillLabel(skill).toLowerCase()} and try again.`;
}

export const QUESTION_TYPES = ['single', 'multiple', 'true_false'] as const;
export const questionTypeSchema = z.enum(QUESTION_TYPES);
export type QuestionType = z.infer<typeof questionTypeSchema>;

export const optionSchema = z.object({
  id: z.string().min(1).max(20),
  text: z.string().trim().min(1).max(500),
  correct: z.boolean(),
});
export type QuestionOption = z.infer<typeof optionSchema>;

export const questionInputSchema = z
  .object({
    type: questionTypeSchema,
    prompt: z.string().trim().min(5).max(2000),
    options: z.array(optionSchema).min(2).max(8),
    explanation: z.string().trim().max(2000).optional(),
    skill: skillSchema,
    difficulty: z.number().int().min(1).max(3).default(2),
  })
  .superRefine((q, ctx) => {
    const correct = q.options.filter((o) => o.correct).length;
    if (q.type === 'multiple' ? correct < 1 : correct !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message:
          q.type === 'multiple'
            ? 'Mark at least one correct answer'
            : 'Mark exactly one correct answer',
      });
    }
    if (q.type === 'true_false' && q.options.length !== 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message: 'True/false questions have two options',
      });
    }
    if (new Set(q.options.map((o) => o.id)).size !== q.options.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message: 'Option ids must be unique',
      });
    }
  });
export type QuestionInput = z.infer<typeof questionInputSchema>;

export const QUIZ_MODES = ['self_assessment', 'assessment'] as const;
export const quizModeSchema = z.enum(QUIZ_MODES);
export type QuizMode = z.infer<typeof quizModeSchema>;

/**
 * `public`: anyone with the link, no account needed. `org`: members of the
 * owner org's tree. `course`: a course's final assessment, only inside that
 * course: never listed or shared, and open once its required lessons are done.
 */
/** Content audiences (see audience.ts), plus `course`: a course's final assessment. */
export const QUIZ_AUDIENCES = [...AUDIENCES, 'course'] as const;

/** Failed attempts in a row after which a course assessment waits COURSE_ASSESSMENT_COOLDOWN_HOURS. */
export const COURSE_ASSESSMENT_MAX_FAILS = 3;
export const COURSE_ASSESSMENT_COOLDOWN_HOURS = 24;
export const quizAudienceSchema = z.enum(QUIZ_AUDIENCES);
export type QuizAudience = z.infer<typeof quizAudienceSchema>;

export const quizSettingsSchema = z.object({
  shuffleQuestions: z.boolean().default(false),
  passPercent: z.number().int().min(0).max(100).default(70),
  certificate: z.boolean().default(true),
  maxAttempts: z.number().int().min(1).max(100).nullable().default(null),
});
export type QuizSettings = z.infer<typeof quizSettingsSchema>;

export const quizInputSchema = z.object({
  title: z.string().trim().min(3).max(200),
  description: z.string().trim().max(2000).optional(),
  mode: quizModeSchema.default('self_assessment'),
  audience: quizAudienceSchema.default('org'),
  audienceScope: audienceScopeSchema.nullable().optional(),
  reuse: reusePolicySchema.nullable().optional(),
  /** A customized quiz: source questions to leave out (its own are `questionIds`). */
  hiddenQuestionIds: z.array(z.string().uuid()).max(500).optional(),
  settings: quizSettingsSchema.default({}),
  level: levelSchema.default('beginner'),
  topics: topicsSchema,
  tags: tagsSchema,
  questionIds: z.array(z.string().uuid()).max(100).default([]),
});
export type QuizInput = z.infer<typeof quizInputSchema>;

export const answerSchema = z.object({
  questionId: z.string().min(1),
  optionIds: z.array(z.string().min(1)).min(1).max(8),
});
export type Answer = z.infer<typeof answerSchema>;

export const submitAttemptSchema = z.object({
  answers: z.array(answerSchema).max(100),
  /** The learner's zone at submission; fixes a certificate's issue date. */
  timeZone: timeZoneSchema.optional(),
});

/** A question as frozen into a published quiz version (answers included; never sent to takers). */
export interface FrozenQuestion {
  id: string;
  type: QuestionType;
  prompt: string;
  options: QuestionOption[];
  explanation: string | null;
  skill: Skill;
  difficulty: number;
}

/** The same question with correctness stripped, for people taking the quiz. */
export type TakerQuestion = Omit<FrozenQuestion, 'options' | 'explanation'> & {
  options: Array<Pick<QuestionOption, 'id' | 'text'>>;
};

export function toTakerQuestion(q: FrozenQuestion): TakerQuestion {
  return {
    id: q.id,
    type: q.type,
    prompt: q.prompt,
    skill: q.skill,
    difficulty: q.difficulty,
    options: q.options.map(({ id, text }) => ({ id, text })),
  };
}

/** An answer is right only when the chosen set equals the correct set exactly. */
export function isCorrect(q: FrozenQuestion, optionIds: readonly string[]): boolean {
  const correct = q.options
    .filter((o) => o.correct)
    .map((o) => o.id)
    .sort();
  const chosen = [...new Set(optionIds)].sort();
  return correct.length === chosen.length && correct.every((id, i) => id === chosen[i]);
}

export interface SkillScore {
  correct: number;
  total: number;
  percent: number;
}

export interface QuizResult {
  score: number;
  maxScore: number;
  percent: number;
  passed: boolean;
  skills: Partial<Record<Skill, SkillScore>>;
  /** Weakest first, only skills under 70%. */
  focusAreas: Skill[];
}

/** Scores answers against frozen questions. Unanswered questions count as wrong. */
export function scoreAttempt(
  questions: readonly FrozenQuestion[],
  answers: readonly Answer[],
  passPercent: number,
): QuizResult {
  const byId = new Map(answers.map((a) => [a.questionId, a.optionIds]));
  const skills: Partial<Record<Skill, SkillScore>> = {};
  let score = 0;
  for (const q of questions) {
    const ok = isCorrect(q, byId.get(q.id) ?? []);
    if (ok) score++;
    const s = (skills[q.skill] ??= { correct: 0, total: 0, percent: 0 });
    s.total++;
    if (ok) s.correct++;
  }
  for (const s of Object.values(skills)) s!.percent = Math.round((s!.correct / s!.total) * 100);
  const percent = questions.length ? Math.round((score / questions.length) * 100) : 0;
  const focusAreas = (Object.entries(skills) as Array<[Skill, SkillScore]>)
    .filter(([, s]) => s.percent < 70)
    .sort((a, b) => a[1].percent - b[1].percent)
    .map(([k]) => k);
  return {
    score,
    maxScore: questions.length,
    percent,
    passed: percent >= passPercent,
    skills,
    focusAreas,
  };
}

/** A named collection of questions (a topic, technology or vertical). */
export const questionBankInputSchema = z.object({
  title: z.string().trim().min(3).max(120),
  description: z.string().trim().max(1000).optional(),
});
export type QuestionBankInput = z.infer<typeof questionBankInputSchema>;
